import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect, useRef, useCallback, forwardRef, useState } from 'react';

import { Icon } from '../Icon';

import type {
  MessageProps,
  MessageVariant,
  MessageSize,
  MessageDisplayMode,
  MessageTextColor,
  InternalSnackbarProps,
} from './Message.types';
import type { Theme } from '../../theme';
import type { IconColor } from '../Icon';

/**
 * Configuration for each message variant
 */
const variantConfig: Record<
  MessageVariant,
  {
    iconName: string;
    iconColor: IconColor;
    getContextColor: (theme: Theme) => string;
    getBannerBackground: (theme: Theme) => string;
    getBannerBorderColor: (theme: Theme) => string;
    role: 'alert' | 'status' | undefined;
  }
> = {
  info: {
    iconName: 'info',
    iconColor: 'info',
    getContextColor: (theme: Theme) => theme.colors.semantic.blue.base,
    getBannerBackground: (theme: Theme) => theme.colors.semantic.blue.lighter,
    getBannerBorderColor: (theme: Theme) => theme.colors.semantic.blue.base,
    role: 'status',
  },
  error: {
    iconName: 'error',
    iconColor: 'error',
    getContextColor: (theme: Theme) => theme.colors.semantic.red.base,
    getBannerBackground: (theme: Theme) => theme.colors.semantic.red.lighter,
    getBannerBorderColor: (theme: Theme) => theme.colors.semantic.red.base,
    role: 'alert',
  },
  warning: {
    iconName: 'warning',
    iconColor: 'warning',
    getContextColor: (theme: Theme) => theme.colors.semantic.orange.base,
    getBannerBackground: (theme: Theme) => theme.colors.semantic.orange.lighter,
    getBannerBorderColor: (theme: Theme) => theme.colors.semantic.orange.base,
    role: 'status',
  },
  success: {
    iconName: 'check_circle',
    iconColor: 'success',
    getContextColor: (theme: Theme) => theme.colors.semantic.green.base,
    getBannerBackground: (theme: Theme) => theme.colors.semantic.green.lighter,
    getBannerBorderColor: (theme: Theme) => theme.colors.semantic.green.base,
    role: 'status',
  },
  assistive: {
    iconName: 'help',
    iconColor: 'muted',
    getContextColor: (theme: Theme) => theme.colors.text.secondary,
    getBannerBackground: (theme: Theme) => theme.colors.grey[100],
    getBannerBorderColor: (theme: Theme) => theme.colors.grey[400],
    role: undefined,
  },
};

// Animation keyframes for snackbar
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

/**
 * Base container for inline messages
 */
const InlineContainer = styled.div`
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
`;

/**
 * Banner container with background and border
 */
const BannerContainer = styled.div<{
  theme?: Theme;
  $variant: MessageVariant;
  $maxWidth?: string | number;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid;
  margin-bottom: 16px;
  background-color: ${({ theme, $variant }) =>
    variantConfig[$variant].getBannerBackground(theme)};
  border-left-color: ${({ theme, $variant }) =>
    variantConfig[$variant].getBannerBorderColor(theme)};
  ${({ $maxWidth }) =>
    $maxWidth
      ? `max-width: ${typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth};`
      : ''}
`;

/**
 * Snackbar container with animation
 */
const SnackbarContainer = styled.div<{
  theme?: Theme;
  $variant: MessageVariant;
  $isExiting?: boolean;
  $maxWidth?: string | number;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  max-width: ${({ $maxWidth }) =>
    $maxWidth
      ? typeof $maxWidth === 'number'
        ? `${$maxWidth}px`
        : $maxWidth
      : '500px'};
  background-color: ${({ theme, $variant }) =>
    variantConfig[$variant].getBannerBackground(theme)};
  border-left-color: ${({ theme, $variant }) =>
    variantConfig[$variant].getBannerBorderColor(theme)};
  animation: ${(props) => (props.$isExiting ? slideOut : slideIn)} 0.3s ease-out;
  animation-fill-mode: forwards;
`;

/**
 * Wrapper for the icon
 */
const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

/**
 * Inline icon wrapper (smaller margin)
 */
const InlineIconWrapper = styled(IconWrapper)`
  margin-right: 4px;
`;

/**
 * Content wrapper for banner/snackbar
 */
const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/**
 * Wrapper for toggle button to position it bottom-right
 */
const ToggleWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;

/**
 * Message text with variant-based coloring
 */
const MessageText = styled.span<{
  theme?: Theme;
  $variant: MessageVariant;
  $size: MessageSize;
  $textColor: MessageTextColor;
  $customColor?: string;
  $mode: MessageDisplayMode;
  $truncateLines?: number;
  $isExpanded?: boolean;
}>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $size }) => {
    switch ($size) {
      case 'tn':
        return theme.typography.fontSize.tn;
      case 'xs':
        return theme.typography.fontSize.xs;
      case 'md':
        return theme.typography.fontSize.md;
      case 'sm':
      default:
        return theme.typography.fontSize.sm;
    }
  }};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme, $variant, $textColor, $customColor }) => {
    if ($textColor === 'custom' && $customColor) {
      return $customColor;
    }
    if ($textColor === 'default') {
      return theme.colors.text.primary;
    }
    // context color
    return variantConfig[$variant].getContextColor(theme);
  }};
  word-break: break-word;
  
  /* Line clamping for truncation */
  ${({ $truncateLines, $isExpanded }) =>
    $truncateLines && $truncateLines > 0 && !$isExpanded
      ? `
    display: -webkit-box;
    -webkit-line-clamp: ${$truncateLines};
    -webkit-box-orient: vertical;
    overflow: hidden;
  `
      : ''}
`;

/**
 * Toggle button for Show More / Show Less
 */
const ToggleButton = styled.button<{
  theme?: Theme;
  $variant: MessageVariant;
  $size: MessageSize;
}>`
  background: none;
  border: none;
  padding: 0;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $size }) => {
    switch ($size) {
      case 'tn':
        return theme.typography.fontSize.tn;
      case 'xs':
        return theme.typography.fontSize.xs;
      case 'md':
        return theme.typography.fontSize.md;
      case 'sm':
      default:
        return theme.typography.fontSize.sm;
    }
  }};
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme, $variant }) => variantConfig[$variant].getContextColor(theme)};

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

/**
 * Action button for snackbar
 */
const ActionButton = styled.button<{ theme?: Theme; $variant: MessageVariant }>`
  background: none;
  border: none;
  padding: 4px 8px;
  margin: 4px 0 0 -8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
  color: ${({ theme, $variant }) => variantConfig[$variant].getContextColor(theme)};
  align-self: flex-start;
  width: fit-content;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

/**
 * Close button for snackbar
 */
const CloseButton = styled.button<{ theme?: Theme; $variant: MessageVariant }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $variant }) => variantConfig[$variant].getContextColor(theme)};
  opacity: 0.7;
  transition: opacity 0.2s, background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

/**
 * Message - Unified message component for displaying various types of feedback
 *
 * Supports multiple variants: info, error, warning, and success.
 * Supports multiple display modes: inline, banner, and snackbar.
 * Each variant has its own default icon, color, and accessibility role.
 *
 * @example
 * ```tsx
 * // Inline error message (default)
 * <Message variant="error">This field is required</Message>
 *
 * // Banner message with background
 * <Message variant="warning" mode="banner">This action cannot be undone</Message>
 *
 * // Snackbar message with timeout
 * <Message
 *   variant="success"
 *   mode="snackbar"
 *   onClose={() => setOpen(false)}
 *   action={{ label: 'Undo', onClick: handleUndo }}
 * >
 *   Your changes have been saved
 * </Message>
 *
 * // Message with default (black) text color
 * <Message variant="info" textColor="default">Information</Message>
 *
 * // Message without icon
 * <Message variant="error" showIcon={false}>Error text only</Message>
 * ```
 */
export const Message = forwardRef<HTMLDivElement, MessageProps | InternalSnackbarProps>(
  (
    {
      variant,
      children,
      mode = 'inline',
      size = 'sm',
      textColor = 'context',
      customTextColor,
      icon,
      showIcon = true,
      open = true,
      autoHideDuration = 5000,
      onClose,
      action,
      showCloseButton = true,
      maxWidth,
      truncateLines,
      showMoreText = 'Show More',
      showLessText = 'Show Less',
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
      ...rest
    },
    ref
  ) => {
    const isExiting = (rest as InternalSnackbarProps).isExiting;
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [needsTruncation, setNeedsTruncation] = useState(false);

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    // Auto-hide timer for snackbar mode
    useEffect(() => {
      if (mode === 'snackbar' && open && autoHideDuration !== null && autoHideDuration > 0) {
        timerRef.current = setTimeout(() => {
          handleClose();
        }, autoHideDuration);
      }

      return () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
      };
    }, [mode, open, autoHideDuration, handleClose]);

    // Check if text needs truncation (only check once when not expanded)
    useEffect(() => {
      if (truncateLines && truncateLines > 0 && textRef.current && !isExpanded) {
        const element = textRef.current;
        const wouldBeTruncated = element.scrollHeight > element.clientHeight;
        if (wouldBeTruncated) {
          setNeedsTruncation(true);
        }
      }
    }, [truncateLines, children, isExpanded]);

    const handleMouseEnter = useCallback(() => {
      if (mode === 'snackbar' && timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    }, [mode]);

    const handleMouseLeave = useCallback(() => {
      if (mode === 'snackbar' && open && autoHideDuration !== null && autoHideDuration > 0) {
        timerRef.current = setTimeout(() => {
          handleClose();
        }, autoHideDuration);
      }
    }, [mode, open, autoHideDuration, handleClose]);

    const handleToggleExpand = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    // Handle empty or whitespace-only string children
    if (!children || (typeof children === 'string' && children.trim() === '')) {
      return null;
    }

    // For snackbar mode, don't render if not open
    if (mode === 'snackbar' && !open && !isExiting) {
      return null;
    }

    const config = variantConfig[variant];

    const renderIcon = () => {
      if (!showIcon) return null;

      if (icon) {
        return mode === 'inline' ? (
          <InlineIconWrapper aria-hidden="true">{icon}</InlineIconWrapper>
        ) : (
          <IconWrapper aria-hidden="true">{icon}</IconWrapper>
        );
      }

      const iconElement = <Icon name={config.iconName} size="sm" color={config.iconColor} />;

      return mode === 'inline' ? (
        <InlineIconWrapper aria-hidden="true">{iconElement}</InlineIconWrapper>
      ) : (
        <IconWrapper aria-hidden="true">{iconElement}</IconWrapper>
      );
    };

    const renderMessage = () => (
      <MessageText
        ref={textRef}
        $variant={variant}
        $size={size}
        $textColor={textColor}
        $customColor={customTextColor}
        $mode={mode}
        $truncateLines={truncateLines}
        $isExpanded={isExpanded}
        role={mode === 'inline' ? config.role : undefined}
      >
        {children}
      </MessageText>
    );

    const renderToggleButton = () => {
      // Show toggle button if truncation is enabled AND text needs truncation OR is currently expanded
      if (!truncateLines || truncateLines <= 0 || (!needsTruncation && !isExpanded)) {
        return null;
      }
      return (
        <ToggleWrapper>
          <ToggleButton
            $variant={variant}
            $size={size}
            onClick={handleToggleExpand}
            type="button"
            aria-expanded={isExpanded}
          >
            {isExpanded ? showLessText : showMoreText}
          </ToggleButton>
        </ToggleWrapper>
      );
    };

    // Inline mode
    if (mode === 'inline') {
      return (
        <InlineContainer
          ref={ref}
          className={className}
          data-testid={dataTestId || testId}
          data-id={dataId}
        >
          {renderIcon()}
          {renderMessage()}
        </InlineContainer>
      );
    }

    // Banner mode
    if (mode === 'banner') {
      return (
        <BannerContainer
          ref={ref}
          $variant={variant}
          $maxWidth={maxWidth}
          className={className}
          role={config.role === 'alert' ? 'alert' : 'status'}
          data-testid={dataTestId || testId}
          data-id={dataId}
        >
          {renderIcon()}
          <ContentWrapper>
            {renderMessage()}
            {renderToggleButton()}
          </ContentWrapper>
        </BannerContainer>
      );
    }

    // Snackbar mode
    return (
      <SnackbarContainer
        ref={ref}
        $variant={variant}
        $isExiting={isExiting}
        $maxWidth={maxWidth}
        className={className}
        role="alert"
        aria-live="polite"
        data-testid={dataTestId || testId}
        data-id={dataId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {renderIcon()}
        <ContentWrapper>
          {renderMessage()}
          {renderToggleButton()}
          {action && (
            <ActionButton $variant={variant} onClick={action.onClick} type="button">
              {action.label}
            </ActionButton>
          )}
        </ContentWrapper>
        {showCloseButton && (
          <CloseButton
            $variant={variant}
            onClick={handleClose}
            type="button"
            aria-label="Close notification"
          >
            <Icon name="close" size="sm" color="inherit" />
          </CloseButton>
        )}
      </SnackbarContainer>
    );
  }
);

Message.displayName = 'Message';

export default Message;
